/**
 * Encode/decode helpers for Voluum path-group conditions.
 *
 * Decodes raw `VoluumPathGroupConditions` into editor rows; re-encodes them
 * into Voluum-shape objects that satisfy `validateFlowUpdate`. Unknown
 * branches and read-only dimensions (`authorable: false`) round-trip
 * untouched.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { VoluumPathGroupConditions } from '@trafficpulse/shared';

import {
  CONDITION_REGISTRY,
  getConditionDescriptor,
} from './condition-registry';
import type {
  ConditionDescriptor,
  ConditionPredicate,
  ConditionRowState,
} from './condition-types';

const KNOWN_KEYS = new Set(CONDITION_REGISTRY.map((d) => d.key));
const AUTHORABLE_KEYS = new Set(
  CONDITION_REGISTRY.filter((d) => d.authorable).map((d) => d.key),
);

function asObject(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

const VALUE_LESS_KINDS = new Set(['flag', 'time-of-day', 'unsupported']);

function decodeValues(
  descriptor: ConditionDescriptor,
  raw: Record<string, unknown>,
): readonly string[] {
  if (VALUE_LESS_KINDS.has(descriptor.valueKind)) return [];
  const value = raw[descriptor.valueField];
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object' && 'code' in entry) {
        return String((entry as { code: unknown }).code);
      }
      return null;
    })
    .filter((s): s is string => typeof s === 'string' && s.length > 0);
}

function decodePredicate(raw: Record<string, unknown>): ConditionPredicate {
  return raw.predicate === 'MUST_NOT_BE' ? 'MUST_NOT_BE' : 'MUST_BE';
}

export function decodeConditions(
  conditions: VoluumPathGroupConditions | undefined,
): ConditionRowState[] {
  if (!conditions) return [];
  return CONDITION_REGISTRY
    .map((descriptor) => {
      const raw = asObject(conditions[descriptor.key]);
      if (!raw) return null;
      const values = decodeValues(descriptor, raw);
      const tzObj = asObject(raw.timeZone);
      const tzCode =
        typeof tzObj?.code === 'string' && tzObj.code.length > 0
          ? tzObj.code
          : undefined;
      const rowBase = {
        descriptor,
        predicate: decodePredicate(raw),
        values,
      };
      if (descriptor.valueKind === 'weekdays') {
        return {
          ...rowBase,
          timeZoneCode: tzCode ?? 'UTC',
        } satisfies ConditionRowState;
      }
      return rowBase satisfies ConditionRowState;
    })
    .filter((r): r is ConditionRowState => r !== null);
}

const DEFAULT_TZ = { code: 'UTC' } as const;

function encodeValues(
  row: ConditionRowState,
  prior: Record<string, unknown>,
): Record<string, unknown> {
  const { descriptor, values } = row;
  if (descriptor.valueKind === 'flag') return {};
  if (descriptor.valueKind === 'time-of-day') {
    return {
      startTime: asObject(prior.startTime) ?? { hours: 0, minutes: 0 },
      endTime: asObject(prior.endTime) ?? { hours: 23, minutes: 59 },
      timeZone: asObject(prior.timeZone) ?? DEFAULT_TZ,
    };
  }
  if (descriptor.valueKind === 'weekdays') {
    const priorTz = asObject(prior.timeZone);
    const fromPrior =
      typeof priorTz?.code === 'string' && priorTz.code.length > 0
        ? priorTz.code
        : undefined;
    const code =
      row.timeZoneCode && row.timeZoneCode.length > 0
        ? row.timeZoneCode
        : (fromPrior ?? DEFAULT_TZ.code);
    return {
      weekdays: [...values],
      timeZone: { code },
    };
  }
  if (
    descriptor.valueKind === 'country-codes' ||
    descriptor.valueKind === 'language-codes'
  ) {
    return { [descriptor.valueField]: values.map((code) => ({ code })) };
  }
  return { [descriptor.valueField]: [...values] };
}

export function encodeRow(
  row: ConditionRowState,
  prior: VoluumPathGroupConditions,
): Record<string, unknown> {
  const priorRaw = asObject(prior[row.descriptor.key]) ?? {};
  return {
    ...priorRaw,
    ...encodeValues(row, priorRaw),
    ...(row.descriptor.predicateSupported ? { predicate: row.predicate } : {}),
  };
}

function encodeNonAuthorableRow(
  row: ConditionRowState,
  prior: VoluumPathGroupConditions,
): Record<string, unknown> | null {
  const priorRaw = asObject(prior[row.descriptor.key]);
  if (!priorRaw) return null;
  return row.descriptor.predicateSupported
    ? { ...priorRaw, predicate: row.predicate }
    : { ...priorRaw };
}

export function applyRows(
  prior: VoluumPathGroupConditions,
  rows: readonly ConditionRowState[],
): VoluumPathGroupConditions {
  const next: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(prior ?? {})) {
    if (!KNOWN_KEYS.has(k) || !AUTHORABLE_KEYS.has(k)) next[k] = v;
  }
  for (const row of rows) {
    if (!row.descriptor.authorable) {
      const preserved = encodeNonAuthorableRow(row, prior);
      if (preserved) next[row.descriptor.key] = preserved;
      continue;
    }
    next[row.descriptor.key] = encodeRow(row, prior);
  }
  return next;
}

export { getConditionDescriptor };
